import type { Editor } from '@tiptap/react';

import type { MouseEvent } from 'react';
import { Fragment, useMemo, useState } from 'react';
import Icon from '../components/Icon';
import { PyMuiButton, PyMuiMenu, PyMuiMenuItem } from '../components/generalComponents';
import IcoArrowDropDownRounded from '../../../../icon/icons/IcoArrowDropDownRounded';
import { DEFAULT_SIZE, headingTextLevels, PARAGRAPH_INITIAL_LEVEL } from 'components/editor/grid/reduxStore/defaultTextDefaultValue';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import EditRounded from '@mui/icons-material/EditRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { EditorDefaultSidePanelType, setActiveSidePanel } from '../../../grid/reduxStore/blockStyleSettingsSlice.ts';
import { useAppDispatch } from '../../../grid/reduxStore/Store.ts';
import { useTranslation } from 'react-i18next';
import { useSpotStyleChecker } from '../hooks/useSpotStyleChecker.ts';
import { TextSelection } from 'prosemirror-state';
import type { Level } from '../extensions/custom-heading';
import type { RowTypesIdentifier, TotalRowTypesIdentifier } from '../../../grid/reduxStore/table.types.ts';
import { headingTableLevels } from '../../../grid/reduxStore/tableDefaultStyleValues.ts';

export type HeadingLevelProps = {
  level?: Level;
  fontSize: string;
  title: string;
  isFontWeightBold: boolean;
  rowType?: RowTypesIdentifier | TotalRowTypesIdentifier;
  dataAttribute?: string;
};

const allActiveLevels = {
  ...headingTextLevels,
  ...headingTableLevels,
};

type Props = {
  editor: Editor | null;
  disabled?: boolean;
};

const Heading = ({ editor, disabled = false }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { isSpotStyleActive, selectedLevel, setSelectedLevel } = useSpotStyleChecker(editor);

  const selectedLevelLabel = useMemo(() => {
    if (selectedLevel === undefined) return '';

    const selectedLevelTitle =
      selectedLevel === PARAGRAPH_INITIAL_LEVEL ? t('editor.toolbar.normal_text') : t(allActiveLevels?.[selectedLevel]?.title);

    return isSpotStyleActive ? `${selectedLevelTitle}*` : selectedLevelTitle;
  }, [selectedLevel, isSpotStyleActive]);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleSelectHeading = (selectedLevel?: string) => {
    if (!selectedLevel) return;

    if (editor?.state.selection.empty) {
      const { state, view } = editor;
      const { $from } = state.selection;
      const node = $from.node();

      if (node) {
        const transaction = state.tr.setSelection(TextSelection.create(state.doc, $from.start(), $from.end()));
        view.dispatch(transaction);
      }
    }
    const headingLevel = allActiveLevels[selectedLevel].level;
    headingLevel
      ? editor?.chain().focus().setDefaultHeading({ level: headingLevel }).run()
      : editor?.chain().focus().setCustomParagraph({ rowType: selectedLevel }).run();
    setSelectedLevel(selectedLevel);
    handleCloseMenu();
  };

  const handleDefaultStyleClick = () => {
    const activeTextStyle = selectedLevel === PARAGRAPH_INITIAL_LEVEL ? 'p' : selectedLevel;
    dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.FORMAT_SETTINGS_PANEL, activeTextStyle }));
    handleCloseMenu();
  };

  const handleSelectNormalText = () => {
    editor?.chain().focus().setParagraph().run();
    setSelectedLevel(PARAGRAPH_INITIAL_LEVEL);
    handleCloseMenu();
  };

  return (
    <Box component="span" display="flex" alignItems="center" alignSelf="stretch" flexDirection="row" justifyContent="flex-start">
      <PyMuiButton
        testId="heading-button"
        isMenuOpen={Boolean(anchorEl)}
        disabled={disabled}
        onClickAction={handleOpenMenu}
        isActive={selectedLevel !== undefined}
        width={144}
      >
        <span style={{ fontSize: 12, minWidth: 112, textAlign: 'left' }}>{selectedLevelLabel}</span>
        <Icon>
          <IcoArrowDropDownRounded />
        </Icon>
      </PyMuiButton>
      <PyMuiMenu
        testId="heading-menu"
        id="select-heading-menu"
        menuListProps="select-heading-button"
        anchorEl={anchorEl}
        isMenuOpen={Boolean(anchorEl)}
        handleClose={handleCloseMenu}
      >
        <PyMuiMenuItem
          testId="normal-text"
          menuItem={
            <Box display="flex" alignItems="center" gap={1.5}>
              {selectedLevel === PARAGRAPH_INITIAL_LEVEL ? (
                <CheckRounded fontSize="medium" sx={{ color: 'action.active' }} />
              ) : (
                <Box sx={{ width: 24 }} />
              )}
              {t('editor.toolbar.normal_text')}
            </Box>
          }
          onClick={handleSelectNormalText}
          fontSize={parseInt(DEFAULT_SIZE)}
        />
        {Object.keys(allActiveLevels).map((accordion) => {
          const { level, dataAttribute, isFontWeightBold, fontSize, title } = allActiveLevels[accordion];
          const testId = `heading-menu-item-${level || dataAttribute?.replace(/_/g, '-')}`;
          return (
            <PyMuiMenuItem
              key={accordion}
              testId={testId}
              onClick={() => handleSelectHeading(dataAttribute)}
              isActive={selectedLevel === dataAttribute}
              menuItem={
                <Box display="flex" alignItems="center" gap={1.5}>
                  {selectedLevel === dataAttribute ? (
                    <CheckRounded fontSize="medium" sx={{ color: 'action.active' }} />
                  ) : (
                    <Box sx={{ width: 24 }} />
                  )}
                  {t(title)}
                </Box>
              }
              fontSize={parseInt(fontSize)}
              isFontWeightBold={isFontWeightBold}
            />
          );
        })}
        <Fragment>
          <Divider />
          <PyMuiMenuItem
            testId="edit-default-style"
            menuItem={
              <Box display="flex" alignItems="center" gap="12px">
                <EditRounded fontSize="medium" sx={{ color: 'action.active' }} />
                {t('editor.toolbar.edit_formatting')}
              </Box>
            }
            onClick={handleDefaultStyleClick}
          />
        </Fragment>
      </PyMuiMenu>
    </Box>
  );
};

export default Heading;
